import { z } from 'zod';

export const socialQualificationFields = [
  'name',
  'phone',
  'interest',
  'city',
  'address',
  'budget',
  'timeframe',
  'preferredTime',
  'branch',
  'requirements',
] as const;
export const socialQualificationFieldSchema = z.enum(socialQualificationFields);
const evidenceSchema = z.object({
  value: z.string().trim().min(1).max(500),
  messageId: z.number().int().positive(),
  quote: z.string().trim().min(1).max(1000),
});

export const socialAssistantSettingsSchema = z.object({
  accounts: z
    .array(
      z.object({
        accountId: z.number().int().positive(),
        commentsEnabled: z.boolean().default(false),
        dmsEnabled: z.boolean().default(false),
        privateRepliesEnabled: z.boolean().default(false),
        qualificationFields: z
          .array(socialQualificationFieldSchema)
          .max(10)
          .nullable()
          .default(null),
        conversionAction: z.string().trim().max(200).default(''),
        postIds: z.array(z.string().trim().min(1).max(191)).max(100).default([]),
        postProducts: z
          .array(
            z.object({
              postId: z.string().trim().min(1).max(191),
              productId: z.number().int().positive(),
              campaign: z.string().trim().max(200).default(''),
            })
          )
          .max(100)
          .default([]),
      })
    )
    .max(50)
    .default([])
    .refine(
      rows => new Set(rows.map(row => row.accountId)).size === rows.length,
      'Configure each account only once.'
    ),
  tone: z.string().trim().max(500).default('Friendly, helpful and concise'),
  language: z.enum(['auto', 'english', 'hindi', 'hinglish']).default('auto'),
  approvedKnowledge: z.string().trim().max(8000).default(''),
  conversionAction: z.string().trim().min(1).max(200).default('Request a callback'),
  requiredFields: z
    .array(socialQualificationFieldSchema)
    .max(10)
    .default(['name', 'phone', 'interest']),
  requireFollowUp: z.boolean().default(false),
  handoverInstructions: z
    .string()
    .trim()
    .max(2000)
    .default('Hand over complaints and sensitive enquiries.'),
  maxRepliesPerWindow: z.number().int().min(1).max(50).default(10),
  maxChargePerReply: z.number().min(0).max(10000).default(0),
});
export type SocialAssistantSettings = z.infer<typeof socialAssistantSettingsSchema>;

export function resolveSocialAssistantSettings(
  settings: SocialAssistantSettings,
  account: SocialAssistantSettings['accounts'][number]
): SocialAssistantSettings {
  return {
    ...settings,
    requiredFields: account.qualificationFields ?? settings.requiredFields,
    conversionAction: account.conversionAction || settings.conversionAction,
  };
}

export const socialAssistantDecisionSchema = z.object({
  intent: z.enum([
    'appreciation',
    'enquiry',
    'question',
    'complaint',
    'spam',
    'irrelevant',
    'opt_out',
  ]),
  reply: z.string().trim().max(1000),
  fields: z
    .array(
      z.object({
        field: socialQualificationFieldSchema,
        ...evidenceSchema.shape,
        remove: z.boolean(),
      })
    )
    .max(10),
  productId: z.number().int().positive().nullable(),
  productEvidence: evidenceSchema.nullable(),
  consent: z.enum(['unknown', 'granted', 'declined', 'revoked']),
  consentEvidence: evidenceSchema.nullable(),
  followUpAt: z.string().max(60).nullable(),
  followUpEvidence: evidenceSchema.nullable(),
  missingFields: z.array(z.string().max(80)).max(12),
  nextAction: z.enum([
    'reply',
    'invite_dm',
    'private_reply',
    'qualify',
    'request_callback',
    'handover',
    'none',
  ]),
  handover: z.boolean(),
  summary: z.string().trim().max(1500),
});
export type SocialAssistantDecision = z.infer<typeof socialAssistantDecisionSchema>;

export const socialAssistantStateSchema = z.object({
  fields: z.record(socialQualificationFieldSchema, evidenceSchema).default({}),
  productId: z.number().int().positive().nullable().default(null),
  productEvidence: evidenceSchema.nullable().default(null),
  consent: z.enum(['unknown', 'granted', 'declined', 'revoked']).default('unknown'),
  consentEvidence: evidenceSchema.nullable().default(null),
  genuineEnquiry: z.boolean().default(false),
  optedOut: z.boolean().default(false),
  handover: z.boolean().default(false),
  followUpAt: z.string().nullable().default(null),
  followUpEvidence: evidenceSchema.nullable().default(null),
  missingFields: z.array(z.string()).default([]),
  summary: z.string().max(1500).default(''),
  lastMessageId: z.number().nullable().default(null),
  lastJobId: z.number().nullable().default(null),
  lastError: z.string().max(1000).nullable().default(null),
  pendingReply: z.string().max(1000).nullable().default(null),
  usage: z
    .object({ model: z.string(), totalTokens: z.number(), charge: z.number(), outcome: z.string() })
    .nullable()
    .default(null),
});
export type SocialAssistantState = z.infer<typeof socialAssistantStateSchema>;

export const SOCIAL_ASSISTANT_DECISION_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    intent: { type: 'string', enum: socialAssistantDecisionSchema.shape.intent.options },
    reply: { type: 'string' },
    fields: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          field: { type: 'string', enum: socialQualificationFields },
          value: { type: 'string' },
          messageId: { type: 'integer' },
          quote: { type: 'string' },
          remove: { type: 'boolean' },
        },
        required: ['field', 'value', 'messageId', 'quote', 'remove'],
      },
    },
    productId: { type: ['integer', 'null'] },
    ...Object.fromEntries(
      ['productEvidence', 'consentEvidence', 'followUpEvidence'].map(key => [
        key,
        {
          type: ['object', 'null'],
          additionalProperties: false,
          properties: {
            value: { type: 'string' },
            messageId: { type: 'integer' },
            quote: { type: 'string' },
          },
          required: ['value', 'messageId', 'quote'],
        },
      ])
    ),
    consent: { type: 'string', enum: ['unknown', 'granted', 'declined', 'revoked'] },
    followUpAt: { type: ['string', 'null'] },
    missingFields: { type: 'array', items: { type: 'string' } },
    nextAction: { type: 'string', enum: socialAssistantDecisionSchema.shape.nextAction.options },
    handover: { type: 'boolean' },
    summary: { type: 'string' },
  },
  required: Object.keys(socialAssistantDecisionSchema.shape),
};
